import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { t } from "i18next";

export const useTodayIntersection = (modelsData) => {
  const [intersectionValues, setIntersectionValues] = useState({
    fullIrrigation: null,
    deficitIrrigation: null,
  });

  useEffect(() => {
    if (!modelsData?.labels) return;
    const today = modelsData.verticalLines.find((line) => line.today)?.today;
    const xIndex = modelsData?.labels.indexOf(today);

    if (xIndex !== -1) {
      const suggestedFullDataset = modelsData.datasets.find(
        (d) => d.label === "Suggested full irrigation",
      );
      const suggestedDeficitDataset = modelsData.datasets.find(
        (d) => d.label === "Suggested deficit irrigation",
      );
      setIntersectionValues({
        fullIrrigation: suggestedFullDataset
          ? suggestedFullDataset.data[xIndex]
          : null,
        deficitIrrigation: suggestedDeficitDataset
          ? suggestedDeficitDataset.data[xIndex]
          : null,
      });
    }
  }, [modelsData]);
  return intersectionValues;
};

export const chartColors = {
  FULL_IRRIGATION: "rgba(44,108,140,255)",
  DEFICIT_IRRIGATION: "rgba(234,123,69,255)",
  SOIL_MOISTURE: "rgba(63,124,152,255)",
  TODAY: "rgba(144, 224, 239, 1)",
  SUGGESTED_DATE: "green",
  DELAYED_DATE: "rgba(239,157,118,255)",
  DEFICIT_DATE: "rgba(243,41,36,255)",
  LIGHT_FULL_IRRIGATION: "rgba(44,108,140,0.3)",
  LIGHT_DEFICIT_IRRIGATION: "rgba(234,123,69,0.3)",
  LIGHT_SOIL_MOISTURE: "rgba(63,124,152,0.3)",
  LIGHT_TODAY: "rgba(144, 224, 239, 0.3)",
  LIGHT_SUGGESTED_DATE: "rgba(0, 128, 0, 0.3)",
  LIGHT_DELAYED_DATE: "rgba(239,157,118,0.3)",
  LIGHT_DEFICIT_DATE: "rgba(243,41,36,0.3)",
};

export const createChartData = (modelsData) => {
  // Dataset di base per le linee
  const baseDatasets = [
    {
      type: "line",
      label: t("real-time-page.suggested_irrigation"),
      borderColor: chartColors.FULL_IRRIGATION,
      borderWidth: 2,
      backgroundColor: chartColors.FULL_IRRIGATION,
      data: modelsData?.datasets?.[0]?.data || [],
      fill: false,
    },
    {
      type: "line",
      label: t("real-time-page.suggested_deficit_irrigation"),
      borderColor: chartColors.DEFICIT_IRRIGATION,
      borderWidth: 2,
      backgroundColor: chartColors.DEFICIT_IRRIGATION,
      data: modelsData?.datasets?.[2]?.data || [],
      fill: false,
    },
    {
      type: "line",
      label: t("real-time-page.soil_moisture"),
      backgroundColor: chartColors.SOIL_MOISTURE,
      borderColor: chartColors.SOIL_MOISTURE,
      borderDash: [5, 5],
      data: modelsData?.datasets?.[4]?.data || [],
      fill: false,
      yAxisID: "yRight",
    },
  ];

  // Dataset fittizi per le linee verticali nella legenda
  const verticalLineDatasets = [
    {
      type: "line",
      label: t("real-time-page.today"),
      borderColor: chartColors.TODAY,
      borderWidth: 4,
      backgroundColor: chartColors.TODAY,
      data: [],
      fill: false,
      pointRadius: 0,
      showLine: false,
    },
    {
      type: "line",
      label: t("real-time-page.suggested_irrigation_date"),
      borderColor: chartColors.SUGGESTED_DATE,
      borderWidth: 2,
      backgroundColor: chartColors.SUGGESTED_DATE,
      data: [],
      fill: false,
      pointRadius: 0,
      showLine: false,
    },
    {
      type: "line",
      label: t("real-time-page.delayed_irrigation_date"),
      borderColor: chartColors.DELAYED_DATE,
      borderWidth: 2,
      backgroundColor: chartColors.DELAYED_DATE,
      data: [],
      fill: false,
      pointRadius: 0,
      showLine: false,
    },
    {
      type: "line",
      label: t("real-time-page.deficit_irrigation_date"),
      borderColor: chartColors.DEFICIT_DATE,
      borderWidth: 2,
      backgroundColor: chartColors.DEFICIT_DATE,
      data: [],
      fill: false,
      pointRadius: 0,
      showLine: false,
    },
  ];

  if (!modelsData?.labels || !modelsData?.datasets) {
    return {
      labels: [],
      datasets: [...baseDatasets, ...verticalLineDatasets],
    };
  }

  return {
    labels: modelsData?.labels.map((date) => dayjs(date).format("DD MMM")),
    datasets: [...baseDatasets, ...verticalLineDatasets],
  };
};

export const createVerticalLinePlugin = (modelsData) => ({
  id: "verticalLinePlugin",
  afterDatasetsDraw: (chart) => {
    if (!modelsData?.verticalLines?.length || !modelsData?.labels?.length)
      return;

    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales) return;

    // Trova la scala X in modo robusto
    const xAxisId = Object.keys(scales).find((k) => scales[k]?.axis === "x");
    const xAxis = xAxisId ? scales[xAxisId] : undefined;
    if (!xAxis) return;

    const verticalLines = [
      {
        date: modelsData.verticalLines[0]?.today,
        color: chartColors.LIGHT_TODAY,
        isToday: true,
      },
      {
        date: modelsData.verticalLines[1]?.earlyIrrigationDate,
        color: chartColors.LIGHT_SUGGESTED_DATE,
      },
      {
        date: modelsData.verticalLines[2]?.lateIrrigationDate,
        color: chartColors.LIGHT_DELAYED_DATE,
      },
      {
        date: modelsData.verticalLines[3]?.limitIrrigationDate,
        color: chartColors.LIGHT_DEFICIT_DATE,
      },
    ].filter((line) => line.date && dayjs(line.date).isValid());

    ctx.save();
    ctx.globalCompositeOperation = "source-over";

    const xPositionMap = {};

    verticalLines.forEach(({ date, color, isToday }) => {
      const xIndex = modelsData.labels.findIndex(
        (lbl) => dayjs(lbl).isValid() && dayjs(lbl).isSame(dayjs(date), "day"),
      );
      if (xIndex === -1) return;

      // Prima prova a prendere la X dal meta (più affidabile con scale category)
      const meta0 = chart.getDatasetMeta(0);
      let x = meta0?.data?.[xIndex]?.x;

      // Fallback: posizione dal tick della scala category
      if (x === undefined || Number.isNaN(x)) {
        x =
          typeof xAxis.getPixelForTick === "function"
            ? xAxis.getPixelForTick(xIndex)
            : undefined;
      }
      if (x === undefined || Number.isNaN(x)) {
        // console.warn("Vertical line: X non calcolabile per index", xIndex);
        return;
      }

      if (!xPositionMap[x]) xPositionMap[x] = 0;
      const offset = xPositionMap[x] * 6; // piccolo offset se più linee cadono sullo stesso giorno
      xPositionMap[x] += 1;

      const adjustedX = x + offset;

      ctx.beginPath();
      ctx.moveTo(adjustedX, chartArea.top);
      ctx.lineTo(adjustedX, chartArea.bottom);
      ctx.lineWidth = isToday ? 4 : 2;
      ctx.strokeStyle = color;
      ctx.stroke();
    });

    ctx.restore();
  },
});

export const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        boxWidth: 20,
        usePointStyle: true,
        generateLabels: function (chart) {
          const datasets = chart.data.datasets;
          return datasets
            .map((dataset, index) => {
              const isVerticalLine =
                dataset.showLine === false && dataset.data.length === 0;
              return {
                text: dataset.label,
                fillStyle: dataset.backgroundColor,
                strokeStyle: dataset.borderColor,
                lineWidth: dataset.borderWidth || 2,
                pointStyle: isVerticalLine ? "line" : "circle",
                datasetIndex: index,
                hidden: false,
              };
            })
            .filter((label) => {
              const dataset = datasets[label.datasetIndex];
              const isVerticalLine =
                dataset.showLine === false && dataset.data.length === 0;
              const hasData = dataset.data.length > 0;
              return hasData || isVerticalLine;
            });
        },
      },
      position: "bottom",
    },
    title: {
      display: false,
      text: "Irrigation activities",
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          let label = context.dataset.label || "";
          if (label) {
            label += ": ";
          }
          if (context.raw !== null) {
            const formattedValue = Number(context.raw).toFixed(2);
            if (context.dataset.yAxisID === "yRight") {
              label += formattedValue + " %";
            } else {
              label += formattedValue + " m³";
            }
          }
          return label;
        },
      },
    },
  },
  scales: {
    x: {
      position: "top",
    },
    y: {
      title: {
        display: true,
        text: "Quantity of water (m³)",
        font: {
          size: 14,
        },
      },
      min: 0,
    },
    yRight: {
      position: "right",
      title: {
        display: true,
        text: "Soil moisture (%)",
        font: {
          size: 14,
        },
      },
      min: 0,
      max: 50,
      ticks: {
        stepSize: 10,
      },
      grid: {
        display: false,
      },
    },
  },
};
